/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* tslint:disable: no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import MsDyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { fireAnalyticsLink } from '../../Utilities/analytics/clickTrack';
import { htmltoText } from '../../Utilities/analytics/htmlToText';
import { firePromotionClick, firePromotionImpression } from '../../Utilities/analytics/promotionClickTrack';
import { navLinkKeywordRemoval } from '../../Utilities/plp-url-utils';
import { getAnalyticsConfig, getPromoAnalyticsConfig } from './analytics';
import { IIMfrmRestAssuredPromisePropsViewProps } from './mfrm-rest-assured-promise';

const MfrmRestAssuredPromiseView: React.FC<IIMfrmRestAssuredPromisePropsViewProps> = props => {
    const {
        restAssuredPromiseContainer,
        imageContainer,
        detailsContainer,
        title,
        text,
        image,
        restAssuredPromiseAnchorTag,
        isPromotion,
        config
    } = props;

    const [paragraphText, setParagraphText] = React.useState<string>('');

    const pagePath = MsDyn365.isBrowser
        ? window.location.href.split(location.host)[1].includes('?')
            ? window.location.href.split(location.host)[1].split('?')[0]
            : window.location.href.split(location.host)[1]
        : '';

    const analyticsConfig = getAnalyticsConfig(pagePath, paragraphText, config.tealiumEvent);
    const promoAnalyticsConfig = getPromoAnalyticsConfig(
        props.config.image,
        restAssuredPromiseAnchorTag,
        pagePath,
        paragraphText,
        config.tealiumPromotion
    );

    const handleAnalytics = () => {
        if (MsDyn365.isBrowser) {
            fireAnalyticsLink({ ...analyticsConfig });
            if (isPromotion) {
                firePromotionClick({ ...promoAnalyticsConfig });
            }

            if (restAssuredPromiseAnchorTag?.href) {
                window.location.href = navLinkKeywordRemoval(restAssuredPromiseAnchorTag.href, props.context.request);
            }
        }
    };

    const [ref, inView] = useInView({
        triggerOnce: true,
        rootMargin: '25px'
    });

    React.useEffect(() => {
        if (MsDyn365.isBrowser && isPromotion && inView) {
            firePromotionImpression({ ...promoAnalyticsConfig });
        }
        setParagraphText(config.paragraph ? htmltoText(config.paragraph.toString()).trim() : '');
    }, [inView, paragraphText]);

    return (
        <Module {...restAssuredPromiseContainer} data-cy={`rap-${paragraphText}`}>
            <Node
                {...restAssuredPromiseAnchorTag}
                href={navLinkKeywordRemoval(restAssuredPromiseAnchorTag?.href, props.context.request)}
                className={restAssuredPromiseAnchorTag ? restAssuredPromiseAnchorTag.className : ''}
                ref={ref}
                // tslint:disable-next-line:jsx-no-lambda
                onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                    event.preventDefault();
                    handleAnalytics();
                }}
            >
                <Node {...imageContainer}>{image}</Node>
                <Node {...detailsContainer}>
                    {title}
                    {text}
                </Node>
            </Node>
        </Module>
    );
};

export default MfrmRestAssuredPromiseView;
